import React, { useEffect, useRef, useState } from 'react';
import { Form, Formik, FormikValues } from 'formik';
import * as Yup from 'yup';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle
} from '@material-ui/core';
import { Alert } from '@material-ui/lab';

import FormikButton from '../../../common/formik/FormikButton';
import FormikGrid from '../../../common/formik/FormikGrid';
import FormikTextField from '../../../common/formik/FormikTextField';
import AppSnackbar, { AppSnackbarProps } from '../../../common/AppSnackbar';

import { useCreateRisk } from '../Risk.hook';
import { Risk, CreateRiskDto } from '../Risk.interface';

const validationSchema = Yup.object({
  title: Yup.string().required('Title is required.')
});

interface SnackbarProps {
  message?: string;
  severity?: AppSnackbarProps['severity'];
}

export interface RiskCreateDialogProps {
  open: boolean;
  onCancel?: () => void;
  onSuccess?: (risk: Risk) => void;
}

const RiskDialog: React.FC<RiskCreateDialogProps> = (
  props: RiskCreateDialogProps
) => {
  const { open, onCancel, onSuccess } = props;
  const formikRef = useRef<FormikValues>();
  const [{ data: risk, error, isLoading }, post] = useCreateRisk();
  const [snackbarProps, setSnackbarProps] = useState<SnackbarProps>({});

  useEffect(() => {
    formikRef.current?.setSubmitting?.(isLoading);
  }, [isLoading]);

  useEffect(() => {
    if (risk) {
      onSuccess?.(risk);
      // show message
      setSnackbarProps({
        ...snackbarProps,
        severity: 'success',
        message: 'Risk created successfully.'
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [risk]);

  return (
    <>
      <Dialog open={open} onClose={onCancel}>
        <Formik
          innerRef={(formik: any) => (formikRef.current = formik)}
          initialValues={
            {
              title: ''
            } as CreateRiskDto
          }
          {...{ validationSchema }}
          onSubmit={post}
        >
          <Form autoComplete='off'>
            <DialogTitle>Create New Risk</DialogTitle>
            <DialogContent>
              <DialogContentText>Enter Risk details</DialogContentText>
              <FormikGrid>
                <FormikTextField
                  autoFocus
                  required
                  type='text'
                  name='title'
                  label='Title'
                  placeholder='Title of Risk'
                />
              </FormikGrid>

              {error && (
                <Alert severity='error'>
                  {error?.message || 'Unable to save Risk details.'}
                </Alert>
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={onCancel}>Cancel</Button>
              <FormikButton>Create</FormikButton>
            </DialogActions>
          </Form>
        </Formik>
      </Dialog>

      <AppSnackbar
        open={!!snackbarProps?.message}
        severity={snackbarProps?.severity}
        onClose={() => setSnackbarProps({ ...snackbarProps, message: '' })}
      >
        {snackbarProps?.message}
      </AppSnackbar>
    </>
  );
};

export default RiskDialog;
